import { useCallback, useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { confirmedRound, newRequestId, validPending } from "./diceModel";

// Only the wager reference/settings are stored locally. Outcomes, balances and
// history always come from the authenticated server. A retry reuses this ID.
export default function useDiceGame(userId, refreshWallet) {
  const [round, setRound] = useState(null), [history, setHistory] = useState([]);
  const [config, setConfig] = useState(null), [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false), [error, setError] = useState("");
  const [syncError, setSyncError] = useState(""), [historyError, setHistoryError] = useState(false);
  const pending = useRef(null), lock = useRef(false), generation = useRef(0);
  const refreshRef = useRef(refreshWallet); refreshRef.current = refreshWallet;
  const storageKey = `pb_dice_pending:${userId}`;
  const active = useCallback(gen => generation.current === gen, []);

  const loadHistory = useCallback(async () => {
    const gen = generation.current;
    try {
      const { data } = await api.get("/casino/dice/history");
      const rows = (Array.isArray(data) ? data : []).map(confirmedRound);
      if (active(gen)) { setHistory(rows); setHistoryError(false); }
      return rows;
    } catch { if (active(gen)) setHistoryError(true); return []; }
  }, [active]);
  const accept = useCallback(async (data, gen) => {
    const value = confirmedRound(data);
    try {
      if (JSON.parse(sessionStorage.getItem(storageKey))?.request_id === value.request_id) sessionStorage.removeItem(storageKey);
    } catch { /* receipt can be replayed safely */ }
    if (active(gen)) {
      pending.current = null; setSyncError(""); setError(""); setRound(value);
      setHistory(old => [value, ...old.filter(h => h.request_id !== value.request_id)].slice(0, 30));
      // A refresh error must never turn a settled wager into another POST.
      try { Promise.resolve(refreshRef.current?.(value.balances)).catch(() => {}); } catch { /* auth layer owns refresh */ }
    }
    return value;
  }, [active, storageKey]);
  const reconcile = useCallback(async (request, gen) => {
    const { data } = await api.get(`/casino/dice/round/${encodeURIComponent(request.request_id)}`);
    if (data.status === "completed") return accept(data, gen);
    if (data.status === "not_placed") {
      sessionStorage.removeItem(storageKey);
      if (active(gen)) { pending.current = null; setSyncError(""); setError("Your last roll was not placed. Your wallet was not charged for it."); }
      return null;
    }
    throw new Error("This roll is still awaiting confirmation.");
  }, [accept, active, storageKey]);

  const connect = useCallback(async () => {
    const gen = generation.current;
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/casino/dice/config");
      if (!Number.isFinite(data.cash_edge_pct) || data.cash_edge_pct < 0 || data.cash_edge_pct > 50) throw new Error();
      if (active(gen)) setConfig(data);
    } catch { if (active(gen)) { setConfig(null); setError("Couldn’t load Dice payouts. Reconnect before rolling."); } }
    if (!active(gen)) return;
    const rows = await loadHistory();
    if (!active(gen)) return;
    if (pending.current) {
      try { await reconcile(pending.current, gen); }
      catch { if (active(gen)) setSyncError("Your last roll is unconfirmed. Resume the same roll before placing another."); }
    } else if (active(gen)) setRound(rows[0] || null);
    if (active(gen)) setLoading(false);
  }, [active, loadHistory, reconcile]);

  useEffect(() => {
    generation.current += 1; lock.current = false; pending.current = null;
    setRound(null); setHistory([]); setSyncError(""); setBusy(false); setConfig(null);
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey));
      if (saved && !validPending(saved)) throw new Error("Invalid pending wager");
      pending.current = saved;
    } catch { setSyncError("Couldn’t read your saved roll. Reload this page before continuing."); }
    connect();
    return () => { generation.current += 1; };
  }, [storageKey, connect]);

  const send = useCallback(async (request) => {
    const gen = generation.current;
    lock.current = true; setBusy(true); setError("");
    try {
      const { data } = await api.post("/casino/dice", request, { timeout: 15000 });
      return await accept(data, gen);
    } catch (e) {
      if (active(gen)) setError(e?.response ? apiError(e.response.data?.detail) : "Connection interrupted. Checking your roll…");
      try { return await reconcile(request, gen); }
      catch { if (active(gen)) setSyncError("Your roll is unconfirmed. Resume the same roll before placing another."); return null; }
    } finally { if (active(gen)) { lock.current = false; setBusy(false); } }
  }, [accept, active, reconcile]);
  const play = useCallback(async (settings) => {
    if (lock.current || loading || !config || syncError || pending.current) return null;
    let request;
    try {
      request = { ...settings, request_id: newRequestId() };
      if (!validPending(request)) throw new Error();
      sessionStorage.setItem(storageKey, JSON.stringify(request));
    } catch { setError("Couldn’t save your roll reference. Check your settings and browser storage before rolling."); return null; }
    pending.current = request;
    return send(request);
  }, [config, loading, send, storageKey, syncError]);
  const resume = useCallback(async () => {
    if (lock.current || loading) return null;
    if (!pending.current) { await connect(); return null; }
    return send(pending.current);
  }, [connect, loading, send]);

  return { round, history, config, busy, loading, error, syncError, historyError,
    pending: pending.current, play, resume, reconnect: connect, loadHistory };
}
